import { AdvisoryHeader, AdvisoryInput, Button, ButtonTile, Incrementer, MoreInfo } from 'components';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { browserHistory } from 'react-router';
import config from '../config';

export default class AdvisoryToolDetailView extends Component {

  /* Lifecycle */

  constructor(props) {
    super(props);

    this.state = {
      answers: {},
    };

    this.backHandler = this.backHandler.bind(this);
    this.resetQuestionCriteria = this.resetQuestionCriteria.bind(this);

    const { route, router } = this.props;
    router.setRouteLeaveHook(route, this.routerWillLeave.bind(this));
  }

  componentDidMount() {
    this.setDefaultState();
    window.addEventListener('popstate', this.resetQuestionCriteria);

    if (!this.props.advisory.toolStarted) browserHistory.push('/advisory-tool/');
  }

  componentDidUpdate(props) {
    if (this.props.params.questionId !== props.params.questionId) {
      this.setDefaultState();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.resetQuestionCriteria);
  }

  /* Handlers */

  routerWillLeave(nextLocation) {
    const { appModalOpen, modals } = this.props;

    if (nextLocation.pathname.indexOf('advisory-tool/') === -1) {
      if (modals.length > 0) {
        return true;
      }
      appModalOpen({
        component: <FormattedMessage id="advisory.detail.leaveConfirmation" />,
        onCancel: () => {},
        onConfirm: () => { browserHistory.push(nextLocation.pathname); },
        title: <FormattedMessage id="advisory.detail.leaveTitle" />,
        type: 'confirm',
      });

      return false;
    }
    return true;
  }

  backHandler() {
    this.resetAnswersState();
  }

  answerHandler(question, answerKey, value) {
    const answer = question.answers[answerKey];

    switch (question.type) {
      default:
      case 'number':
      case 'selectOne':
      case 'selectOneWithInput':
        if (answerKey === 'input') {
          this.setState({ answers: { ...this.state.answers, [answerKey]: value } });
        } else {
          // default next question
          let next = question.next;

          // next question by answer selection
          if (answer.next) next = answer.next;

          // search for next question via nextIf
          if (answer.nextIf) {
            let nextIfValue = answer.nextIf.value;
            if (!Array.isArray(nextIfValue)) {
              nextIfValue = [nextIfValue];
            }
            if (nextIfValue.indexOf(this.props.advisory.criteria[answer.nextIf.for][answer.nextIf.key]) !== -1) {
              next = answer.nextIf.next;
            }
          }

          this.storeCriteria(question, answerKey);
          this.resetAnswersState();
          browserHistory.push(`/advisory-tool/${next}`);
        }
        break;
      case 'selectMultiple':
        if (answerKey === 'none') {
          this.setState({ answers: { [answerKey]: !this.state.answers[answerKey] } });
        } else {
          this.setState({ answers: { ...this.state.answers, [answerKey]: !this.state.answers[answerKey], none: false } });
        }
        break;
    }
  }

  nextHandler(question) {
    this.storeCriteria(question);

    // default next question
    let nextQuestion = question.next;

    // search for next question via nextMap
    if (question.nextMap) {
      question.nextMap.forEach((map) => {
        let allGood = true;
        map.answers.forEach((answer) => {
          if (this.state.answers[answer] !== true) allGood = false;
        });
        if (allGood) nextQuestion = map.next;
      });
    }

    // search for next question via nextIf
    if (question.nextIf && this.props.advisory.criteria[question.nextIf.for][question.nextIf.key] === question.nextIf.value) {
      nextQuestion = question.nextIf.next;
    }

    // search for next question via nextIfInputIsHigherThen
    const inputNumber = this.state.answers.input && parseInt(this.state.answers.input, 10);
    if (question.nextIfInputIsHigherThen && inputNumber && inputNumber > question.nextIfInputIsHigherThen.value) {
      nextQuestion = question.nextIfInputIsHigherThen.next;
    }

    this.resetAnswersState();
    browserHistory.push(`/advisory-tool/${nextQuestion}`);
  }

  /* Methods */

  storeCriteria(question, answerKey) {
    const answer = question.answers && question.answers[answerKey];

    let criteria = {};
    if (question.criteria) criteria = { ...criteria, ...question.criteria };
    if (answer && answer.criteria) criteria = { ...criteria, ...answer.criteria };

    switch (question.type) {
      default:
        break;
      case 'number':
      case 'selectOneWithInput': {
        if (!answerKey) {
          const inputCriteria = {};
          Object.keys(question.answers).forEach((answerLoopKey) => {
            const answserCriteria = question.answers[answerLoopKey].criteria;
            if (answserCriteria && this.state.answers[answerLoopKey]) {
              Object.keys(answserCriteria).forEach((criteriaKey) => {
                if (answserCriteria[criteriaKey] === `{${answerLoopKey}}`) {
                  inputCriteria[criteriaKey] = this.state.answers[answerLoopKey];
                }
              });
            }
          });
          criteria = { ...criteria, ...inputCriteria };
        }
        break;
      }
      case 'selectMultiple': {
        const multipleCriteria = {};
        Object.keys(this.state.answers).forEach((answerStateKey) => {
          if (this.state.answers[answerStateKey]) {
            const answserCriteria = question.answers[answerStateKey].criteria;
            if (answserCriteria) {
              Object.keys(answserCriteria).forEach((criteriaKey) => {
                if (!multipleCriteria[criteriaKey]) multipleCriteria[criteriaKey] = [];
                multipleCriteria[criteriaKey].push(answserCriteria[criteriaKey]);
              });
            }
          }
        });
        criteria = { ...criteria, ...multipleCriteria };
        break;
      }
    }

    this.props.advisoryStoreCriteria({ for: question.for, criteria });
  }

  resetQuestionCriteria() {
    const question = config[this.props.params.questionId];
    const criteriaKeys = [];

    if (question.criteria) criteriaKeys.push(...Object.keys(question.criteria));
    if (question.answers) {
      Object.keys(question.answers).forEach((answerKey) => {
        const answer = question.answers[answerKey];
        if (answer.criteria) criteriaKeys.push(...Object.keys(answer.criteria));
      });
    }

    this.props.advisoryRemoveCriteria({ for: question.for, criteriaKeys });
  }

  setDefaultState() {
    const question = config[this.props.params.questionId];
    if (question && question.answers) {
      let answers = {};
      Object.keys(question.answers).forEach((answerKey) => {
        const answer = question.answers[answerKey];
        answers = { ...answers, [answerKey]: answer.default || '' };
      });
      this.setState({ answers });
    }
  }

  resetAnswersState() {
    this.setState({ answers: {} });
  }

  /* Render */

  renderQuestion(question) {
    return (
      <div className="advisory-question__question">
        <FormattedHTMLMessage id={`advisory.questions.${question.key}.question`} />
        {question.info && this.props.intl.messages[`advisory.questions.${question.key}.info`] && (
          <div>
            <MoreInfo content={(<FormattedHTMLMessage id={`advisory.questions.${question.key}.info`} />)} />
          </div>
        )}
      </div>
    );
  }

  renderAnswer(question, answerKey) {
    let content = null;
    const answer = question.answers[answerKey];
    const textKey = `advisory.questions.${question.key}.answers.${answerKey}`;
    const text = this.props.intl.messages[textKey];
    const htmlText = <FormattedHTMLMessage id={textKey} />;
    const infoKey = `advisory.questions.${question.key}.answers.${answerKey}Info`;
    const infoText = this.props.intl.messages[infoKey];
    const infoContent = answer.info && infoText && (<FormattedHTMLMessage id={infoKey} />);

    switch (question.type) {
      default:
      case 'selectOne':
      case 'selectOneWithInput':
        if (question.type === 'selectOneWithInput' && answerKey === 'input') {
          content = (
            <ButtonTile
              selected={!!this.state.answers[answerKey]}
              info={infoContent}
            >
              <AdvisoryInput
                min={answer.min}
                max={answer.max}
                name={answerKey}
                onChange={e => this.answerHandler(question, answerKey, e.target.value)}
                value={parseFloat(this.state.answers[answerKey], 10)}
                unit={text}
                key={question.key + answerKey}
              />
            </ButtonTile>
          );
        } else {
          content = (
            <ButtonTile
              svg={answer.svg}
              icon={answer.icon && `icon-${answer.icon}`}
              iconSize={answer.icon && `icon--${answer.iconSize || 'xxl'}`}
              onClick={() => this.answerHandler(question, answerKey)}
              info={infoContent}
              text={htmlText}
            />
          );
        }
        break;
      case 'selectMultiple':
        content = (
          <ButtonTile
            svg={answer.svg}
            icon={answer.icon && `icon-${answer.icon}`}
            iconSize={answer.icon && `icon--${answer.iconSize || 'xxl'}`}
            onClick={() => this.answerHandler(question, answerKey)}
            selected={!!this.state.answers[answerKey]}
            info={infoContent}
            text={htmlText}
          />
          );
        break;
      case 'number':
        content = (
          <Incrementer
            min={answer.min}
            max={answer.max}
            name={question.key}
            onChange={e => this.answerHandler(question, answerKey, e)}
            stepSize={answer.step}
            value={this.state.answers[answerKey]}
            unit={text}
            default={answer.default}
          />
        );
        break;
    }

    return (
      <div className="align__cell" key={answerKey}>
        {content}
      </div>
    );
  }

  renderAnswersRow(question, answerKeys, answersRowKey = 0) {
    const content = answerKeys.map((key) => {
      return this.renderAnswer(question, key);
    });

    return (
      <div className="align align--fixed align--gutter-sm align--centered advisory-question__answers" key={answersRowKey}>
        {content}
      </div>
    );
  }

  renderAnswers(question) {
    // render anwserRows; when we have 5 or more answers, split them into rows of 3 answers
    if (question.answers) {
      const answerKeys = Object.keys(question.answers);
      const splitAnswerKeys = [];
      let content = [];
      let row = 0;

      if (answerKeys.length >= 5) {
        for (let index = 0; index < answerKeys.length; index += 1) {
          if (!splitAnswerKeys[row]) splitAnswerKeys[row] = [];
          splitAnswerKeys[row].push(answerKeys[index]);
          if ((index + 1) % 3 === 0) row += 1;
        }

        splitAnswerKeys.forEach((answerKeysRow, index) => {
          content.push(this.renderAnswersRow(question, answerKeysRow, index));
        });
      } else {
        content = this.renderAnswersRow(question, answerKeys);
      }

      return content;
    }
    return null;
  }

  renderNextButton(question) {
    const answerKeys = (question.answers && Object.keys(question.answers)) || [];
    let content = null;
    let disabled = false;

    switch (question.type) {
      default:
      case 'selectOne':
        content = null;
        break;
      case 'end':
        content = (
          <Button
            className="button button-bg-yellow button-full-width button-full-width--with-sidebar"
            text="advisory.detail.end"
            onClick={() => { browserHistory.push('/advisory-tool'); }}
          />
        );
        break;
      case 'info':
      case 'number':
      case 'selectMultiple':
      case 'selectOneWithInput':
        if (answerKeys.length) {
          disabled = !answerKeys.find(key => !!this.state.answers[key]);
        }
        content = (
          <Button
            className="button button-bg-yellow button-full-width button-full-width--with-sidebar"
            text="advisory.detail.next"
            disabled={disabled}
            onClick={() => this.nextHandler(question)}
          />
        );
        break;
    }

    return content;
  }

  render() {
    const question = config[this.props.params.questionId];

    return (
      <div className="view">
        <AdvisoryHeader backHandler={this.backHandler} />
        <div className="view__content">
          <div className="advisory-question">
            <div className="align align--full-height align--fluid">
              <div className="align__cell u-text-center">
                {this.renderQuestion(question)}
                {this.renderAnswers(question)}
              </div>
            </div>
            {this.renderNextButton(question)}
          </div>
        </div>
      </div>
    );
  }

}

AdvisoryToolDetailView.propTypes = {
  advisory: PropTypes.object,
  appModalOpen: PropTypes.func,
  advisoryRemoveCriteria: PropTypes.func,
  advisoryStoreCriteria: PropTypes.func,
  modals: PropTypes.array,
  params: PropTypes.object,
  intl: PropTypes.object,
  router: PropTypes.object,
  route: PropTypes.object,
};



// WEBPACK FOOTER //
// ./js/views/advisory-tool/detail/index.jsx